import { useEffect, useState } from "react";
import { format } from "date-fns";
import { FileArchive, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { periodesRapides } from "@/components/DateField";
import { telecharger } from "@/components/RapportButtons";
import { api, errMsg, moisLabel } from "@/lib/api";

const DOCS = ["rapprochement", "commentaires_pdf", "commentaires_xlsx"];
const moisDernier = () => {
  const p = periodesRapides()[1];
  return { debut: format(p.debut, "yyyy-MM-dd"), fin: format(p.fin, "yyyy-MM-dd") };
};

const ConfirmEnvoi = ({ ouvert, setOuvert, email, libelle, onEnvoyer }) => (
  <AlertDialog open={ouvert} onOpenChange={setOuvert}>
    <AlertDialogContent data-testid="mois-dernier-confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Envoyer les documents de {libelle} ?</AlertDialogTitle>
        <AlertDialogDescription>Un vrai courriel sera envoyé à {email} avec les 3 documents (rapport de rapprochement PDF, commentaires sur opérations PDF, journal Excel), et une copie à votre adresse.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="mois-dernier-confirm-cancel">Annuler</AlertDialogCancel>
        <AlertDialogAction onClick={onEnvoyer} className="bg-emerald-700 hover:bg-emerald-800" data-testid="mois-dernier-confirm-button">Envoyer</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const RapportMoisDernier = ({ onEnvoye }) => {
  const [email, setEmail] = useState(null);
  const [busy, setBusy] = useState("");
  const [ouvert, setOuvert] = useState(false);
  const p = moisDernier();
  const libelle = moisLabel(p.debut.slice(0, 7));
  useEffect(() => { api.get("/comptable").then((r) => setEmail(r.data.email || "")).catch(() => setEmail("")); }, []);

  const telechargerDocs = async () => {
    setBusy("dl");
    try {
      const r = await api.get("/comptable/documents", { params: p, responseType: "blob" });
      telecharger(r.data, `Documents comptables du ${p.debut} au ${p.fin}.zip`);
      toast.success(`Documents de ${libelle} téléchargés (fichier ZIP avec les 3 documents)`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const envoyer = async () => {
    setOuvert(false); setBusy("envoi");
    try {
      const { data } = await api.post("/comptable/envoyer", { ...p, documents: DOCS, message: "" });
      toast.success(`Documents de ${libelle} envoyés à ${data.destinataire}${data.copie ? " (copie à votre adresse)" : ""}`);
      onEnvoye?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="mois-dernier">
      <span className="text-xs text-slate-500" data-testid="mois-dernier-libelle">Rapport de {libelle} :</span>
      <Button variant="outline" size="sm" className="bg-white" disabled={!!busy} onClick={telechargerDocs} data-testid="mois-dernier-telecharger">
        {busy === "dl" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileArchive className="h-4 w-4 mr-2" />}Télécharger
      </Button>
      <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" disabled={!!busy || !email} onClick={() => setOuvert(true)}
        title={email === "" ? "Ajoutez l'adresse du comptable dans Paramètres" : undefined} data-testid="mois-dernier-envoyer">
        {busy === "envoi" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Envoyer au comptable
      </Button>
      <ConfirmEnvoi ouvert={ouvert} setOuvert={setOuvert} email={email} libelle={libelle} onEnvoyer={envoyer} />
    </div>
  );
};
